import React from 'react';
import { useTranslation } from 'react-i18next';
import { ErrorBoundary } from '@/components/errorBoundary';
import { PageNotice } from '@/components/pageNotice';
import { getNodeTestIdProp } from '@/utils/nodeTestProps';
import type { ChartAccount } from '@/api/models';
import { PATH } from '@/router/PATH';
import { ChartOfAccountsPageView } from './ChartOfAccountsPageView';
import { useChartOfAccountsPage } from './useChartOfAccountsPage';

type ChartOfAccountsPageProps = {
    /**
     * Optional override for row activation. Defaults to routing to the Journal
     * scoped to the account.
     */
    onSelectAccount?: (account: ChartAccount) => void;
};

/**
 * `/accounting/accounts` — the Chart of Accounts screen. Owns its data hook and
 * an `ErrorBoundary`; the throw lives in the inner Content child because React
 * boundaries don't catch errors thrown by the component that renders them
 * (matches the repo's self-served page pattern).
 */
export const ChartOfAccountsPage: React.FC<ChartOfAccountsPageProps> = ({
    onSelectAccount,
}) => {
    const page = useChartOfAccountsPage({ onSelectAccount });
    const { t } = useTranslation('pages');

    const copy = React.useMemo(
        () => ({
            errorTitle: t('chartOfAccounts.error.title'),
            errorMessage: t('chartOfAccounts.error.message'),
            errorRetry: t('chartOfAccounts.error.retry'),
        }),
        [t],
    );

    return (
        <div
            className="flex w-full flex-col gap-6"
            {...getNodeTestIdProp('chart-of-accounts-page')}
        >
            <ErrorBoundary
                data-testid="chart-of-accounts-error-boundary"
                fallbackMessage={copy.errorMessage}
                id="chart-of-accounts-error-boundary"
                onRetry={page.onRetry}
                resetKeys={[page.error]}
                retryLabel={copy.errorRetry}
                title={copy.errorTitle}
            >
                <ChartOfAccountsPageContent page={page} />
            </ErrorBoundary>
        </div>
    );
};

type ChartOfAccountsPageContentProps = {
    page: ReturnType<typeof useChartOfAccountsPage>;
};

const ChartOfAccountsPageContent: React.FC<ChartOfAccountsPageContentProps> = ({
    page,
}) => {
    const { t } = useTranslation('pages');

    // A PIN-issued kiosk token 403s on every accounts endpoint whatever role it
    // holds, so explain the requirement rather than rendering a chart whose
    // every request fails. Checked before `error` on purpose: this session never
    // fetched, so there is no error to throw — and a raw 403 would tell the user
    // nothing they can act on.
    if (page.access.kind === 'kiosk') {
        return (
            <PageNotice
                body={t('chartOfAccounts.access.kiosk.message')}
                icon="gravity-ui:lock"
                testId="chart-of-accounts-kiosk-notice"
                title={t('chartOfAccounts.access.kiosk.title')}
                tone="warning"
            />
        );
    }

    if (page.access.kind === 'forbidden') {
        return (
            <PageNotice
                body={t('chartOfAccounts.access.forbidden.message')}
                icon="gravity-ui:lock"
                testId="chart-of-accounts-forbidden-notice"
                title={t('chartOfAccounts.access.forbidden.title')}
                tone="warning"
            />
        );
    }

    if (page.error) {
        throw page.error;
    }

    // A fresh tenant with no chart at all. This is NOT the same as an empty
    // grid: an empty result means "your filter matched nothing", and drawing one
    // here would tell a brand-new tenant their accounting is set up and empty.
    // The two states look identical and mean opposite things, so they must never
    // share a rendering.
    if (page.bootstrapRequired) {
        return (
            <PageNotice
                action={{
                    label: t('chartOfAccounts.bootstrap.action'),
                    to: PATH.ACCOUNTING.CONFIGURATIONS.SETTINGS,
                }}
                body={t('chartOfAccounts.bootstrap.body')}
                icon="gravity-ui:rocket"
                testId="chart-of-accounts-bootstrap-notice"
                title={t('chartOfAccounts.bootstrap.title')}
                tone="info"
            />
        );
    }

    // The View keeps its toolbar (real labels, disabled controls) while loading
    // and skeletons only the grid — the repo's loading-state standard.
    return (
        <ChartOfAccountsPageView
            accounts={page.data ?? []}
            canWrite={page.canWrite}
            drawer={page.drawer}
            expandedKeys={page.expandedKeys}
            isLoading={page.isLoading || page.data === null}
            locale={page.locale}
            onAddAccount={page.onAddAccount}
            onCloseDrawer={page.onCloseDrawer}
            onEditAccount={page.onEditAccount}
            onExpandedChange={page.onExpandedChange}
            onReactivateAccount={page.onReactivateAccount}
            onSavedAccount={page.onSavedAccount}
            onSearchChange={page.onSearchChange}
            onSelectAccount={page.onSelectAccount}
            onToggleArchived={page.onToggleArchived}
            search={page.search}
            showArchived={page.showArchived}
            abnormalNotices={page.abnormalNotices}
            onDismissAbnormal={page.onDismissAbnormal}
            tree={page.tree}
        />
    );
};
